import { useState } from 'react';
import { fmtTs, teamsCallUrl, type EventRecord } from '@lib/care-model';
import { newClientRequestId } from '@lib/care-api';
import { feederLineOf, placeCall } from '@lib/call-guard';
import {
  CALL_OUTCOMES, RECORDED_LINE_SCRIPT, callLog, callLogProblem, callOutcomeLabel, pressSentenceOf,
} from '@lib/quick-keys';

/**
 * SLICE C, img10: THE CALL PANEL on a calling-list entry. His words: "This section is supposed to be a
 * feeder to the calling program - we call members - so make it for this - e.g., check mark - [ ] no
 * answer; [ ] vm; etc. And notes from the call which should be recorded (prior to starting the call and
 * marny says we are on a recorded line)."
 *
 *   1. BEFORE THE CALL: the recorded-line reminder, in the words she says first, and Start call, which
 *      asks the stop check bound to this case (lib/call-guard.ts placeCall; the office, 17 Oct 2026,
 *      interim line 5), then opens Microsoft Teams ready to dial the number and notes when the call
 *      started. A refused check shows its one sentence and never opens Teams.
 *   2. AFTER THE CALL: the boxes in two groups (did not reach / reached, never both), her notes, and the
 *      tick that she said the line is recorded. A call that reached the member cannot be logged without
 *      that tick and without notes (the same rule the server holds, api/src/lib/quick-keys.ts).
 *   3. LOG CALL: one entry in the master (POST /api/care/call-log) with her name. Nothing is sent to the
 *      member. The calls she logs here show below at once; the entry's own list shows calls from the
 *      master once Care reads it.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
export interface CallFeederProps {
  ev: EventRecord;
  locked: boolean;
  /** The member is do-not-solicit or on the suppression list: no dialling from here. */
  blocked: boolean;
}

interface Logged { ts: string; outcomes: string[]; note: string; by: string }

export function CallFeeder({ ev, locked, blocked }: CallFeederProps) {
  const [startedAt, setStartedAt] = useState('');
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [said, setSaid] = useState(false);
  const [crid, setCrid] = useState(() => newClientRequestId());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const [logged, setLogged] = useState<Logged[]>([]);

  const phone = ev.fromPhone || ev.memberSnapshot?.phone1 || '';
  const teams = teamsCallUrl(phone);
  const earlier = (ev.contactLog ?? []).filter((c) => c.channel === 'call');
  const problem = callLogProblem(outcomes, note, said);

  const toggle = (key: string) => setOutcomes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // EVERY MEMBER CALL PASSES THE STOP CHECK (the office, 17 Oct 2026, interim line 5): Start call asks the stop check
  // bound to this case and the line it shows (lib/call-guard.ts placeCall, the same guard as the worklist's Call), and
  // Teams opens only on a plain yes, for the number the server read from the case. A no shows the one sentence and
  // notes no start. placeCall opens its blank tab in the press itself, before any await.
  const [checking, setChecking] = useState(false);
  const [callSaid, setCallSaid] = useState('');
  const startCall = async (): Promise<void> => {
    setChecking(true);
    setCallSaid('');
    try {
      const r = await placeCall(phone, { eventId: ev.eventId, line: feederLineOf(ev) });
      if (r.placed) setStartedAt(new Date().toISOString());
      else setCallSaid(r.said);
    } finally {
      setChecking(false);
    }
  };

  const logIt = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await callLog({ eventId: ev.eventId, outcomes, note: note.trim(), recordedLineSaid: said, ...(startedAt ? { startedAt } : {}), clientRequestId: crid });
      const s = pressSentenceOf(r);
      setResult(s);
      if (s.tone === 'ok') {
        const item = (r.data as { item?: { ts?: string; byName?: string; by?: string } }).item;
        setLogged((prev) => [{ ts: item?.ts ?? new Date().toISOString(), outcomes, note: note.trim(), by: item?.byName || item?.by || 'you' }, ...prev]);
        setOutcomes([]); setNote(''); setSaid(false); setStartedAt('');
        setCrid(newClientRequestId());
      } else if (s.tone === 'danger') {
        setCrid(newClientRequestId());
      }
    } catch (err) {
      setResult({ tone: 'warn', text: `The master did not answer (${(err as Error).message}). Press again: the same call is sent, never twice.` });
    } finally {
      setBusy(false);
    }
  };

  const group = (reached: boolean) => (
    <div style={{ display: 'grid', gap: 2 }}>
      <div className="micro muted">{reached ? 'Reached the member' : 'Did not reach the member'}</div>
      {CALL_OUTCOMES.filter((o) => o.reached === reached).map((o) => (
        <label key={o.key} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={outcomes.includes(o.key)} disabled={locked} onChange={() => toggle(o.key)} /> {o.label}
        </label>
      ))}
    </div>
  );

  return (
    <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--ink-900)' }} data-testid="call-feeder">
      <div className="block-head" style={{ marginBottom: 6 }}>Call</div>

      <div className="note" data-testid="recorded-line-reminder" style={{ borderLeft: '3px solid var(--amber)' }}>
        <div className="micro muted">Before you dial. When the member answers, say first:</div>
        <div style={{ fontSize: '1.05em', marginTop: 2 }}><strong>&ldquo;{RECORDED_LINE_SCRIPT}&rdquo;</strong></div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-sm btn-primary" disabled={locked || blocked || !teams || checking}
            title={blocked ? 'Do not contact: this member is not to be called.' : !teams ? 'No phone number to dial.' : 'Checks the stop list, then opens Microsoft Teams ready to dial this number'}
            data-testid="feeder-call"
            onClick={() => void startCall()}>
            {startedAt ? 'Dial again in Teams' : 'Start call in Teams'}
          </button>
          <span className="small">{phone ? phone.replace(/\D/g, '').replace(/^1?(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3') : 'no number'}</span>
          {startedAt && <span className="micro muted">call started {fmtTs(startedAt)}</span>}
          {callSaid && <span className="micro" role="status" data-testid="feeder-call-refused">{callSaid}</span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, marginTop: 10, flexWrap: 'wrap' }}>
        {group(false)}
        {group(true)}
      </div>

      <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
        <input type="checkbox" checked={said} disabled={locked} onChange={(e) => setSaid(e.target.checked)} />
        I said &ldquo;{RECORDED_LINE_SCRIPT}&rdquo;
      </label>
      <textarea className="text-input" aria-label="Call notes" placeholder="Notes from the call" rows={3} value={note} disabled={locked}
        onChange={(e) => setNote(e.target.value)} style={{ width: '100%', marginTop: 6 }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-sm btn-primary" disabled={locked || busy || !!problem} title={problem || undefined} onClick={() => void logIt()}>
          {busy ? 'Logging...' : 'Log call'}
        </button>
        {problem && outcomes.length > 0 && <span className="micro muted" data-testid="call-problem">{problem}</span>}
      </div>
      {result && <div className={`pill ${result.tone === 'ok' ? 'pill-success' : result.tone === 'warn' ? 'pill-warning' : 'pill-danger'}`} style={{ display: 'block', marginTop: 6, whiteSpace: 'normal' }} data-testid="call-result">{result.text}</div>}

      {(logged.length > 0 || earlier.length > 0) && (
        <div style={{ marginTop: 8 }} data-testid="call-history">
          <div className="micro muted">Calls</div>
          {logged.map((c, i) => (
            <div key={`n${i}`} className="small">{fmtTs(c.ts)} · {c.by} · {c.outcomes.map(callOutcomeLabel).join(', ')}{c.note ? ` · ${c.note}` : ''}</div>
          ))}
          {earlier.map((c, i) => {
            const oc = (c as { outcomes?: string[] }).outcomes;
            return (
              <div key={`e${i}`} className="small">{fmtTs(c.ts)} · {c.byName || c.by || ''}{oc?.length ? ` · ${oc.map(callOutcomeLabel).join(', ')}` : ''}{c.note ? ` · ${c.note}` : ''}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
